// KPI Card Component
// Date: December 4, 2025
// Purpose: Card view for KPI entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChartBarIcon,
  CalendarIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { ActiveStatusIcon } from '@/components/common/ActiveStatusIcon';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface KPICardProps {
  kpi: {
    id: string;
    name: string;
    description?: string;
    current_value?: number;
    tags?: string[] | string;
    unit?: string;
    status?: string;
    last_updated?: string;
    [key: string]: any;
  };
  onView?: (kpi: any) => void;
  onEdit?: (kpi: any) => void;
  onDelete?: (kpi: any) => void;
  onToggleStatus?: (id: string, isActive: boolean) => void | Promise<void>;
  onCopy?: (kpi: any) => void;
  onClick?: (id: string) => void;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  canEdit?: (item: any) => boolean;
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
  }>;
}

export const KPICard: React.FC<KPICardProps> = ({
  kpi,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  onCopy,
  onClick,
  selected,
  onSelectedChange,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (!kpi.id) {
      console.error('KPI ID is missing');
      return;
    }
    
    if (onClick) {
      onClick(kpi.id);
    } else if (onView) {
      onView(kpi);
    } else {
      navigate(`/kpis/${kpi.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  return (
    <Card 
      variant="outline" 
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {onSelectedChange && (
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(selected)}
                onChange={(event) => {
                  event.stopPropagation();
                  onSelectedChange(event.target.checked);
                }}
                onClick={(event) => event.stopPropagation()}
              />
            )}
            <ChartBarIcon className="w-5 h-5 text-[var(--color-accent-text)] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[var(--color-secondary-text)] truncate">
                {kpi.name}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <ActiveStatusIcon
              status={kpi.status}
              onToggle={
                onToggleStatus
                  ? () => onToggleStatus(kpi.id, (kpi.status || '').toUpperCase() !== 'ACTIVE')
                  : undefined
              }
            />
            {/* Action Buttons */}
            <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
              {onView && (
                <button
                  onClick={(e) => handleActionClick(e, () => onView(kpi))}
                  className="p-1.5 text-[var(--color-info)] hover:bg-[var(--color-info)]/10 rounded-md transition-colors"
                  title={t('common.view')}
                  type="button"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              )}
              {onEdit && (canEdit ? canEdit(kpi) : true) && (
                <button
                  onClick={(e) => handleActionClick(e, () => onEdit(kpi))}
                  className="p-1.5 text-[var(--color-success)] hover:bg-[var(--color-success)]/10 rounded-md transition-colors"
                  title={t('common.edit')}
                  type="button"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {onCopy && (
                <button
                  onClick={(e) => handleActionClick(e, () => onCopy(kpi))}
                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                  title={t('common.copy')}
                  type="button"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, () => onDelete(kpi))}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title={t('common.delete')}
                  type="button"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              {customActions.map((action, index) => {
                const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                  ? (action.icon as (item: any) => React.ComponentType<any>)(kpi)
                  : action.icon as React.ComponentType<any>;
                const variant = typeof action.variant === 'function' 
                  ? action.variant(kpi) 
                  : action.variant || 'primary';
                const colorClass = variant === 'danger' 
                  ? 'text-red-600 hover:bg-red-50' 
                  : variant === 'secondary' 
                  ? 'text-gray-600 hover:bg-gray-50' 
                  : 'text-blue-600 hover:bg-blue-50';
                
                return (
                  <button
                    key={index}
                    onClick={(e) => handleActionClick(e, () => action.onClick(kpi))}
                    className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                    title={typeof action.label === 'function' ? action.label(kpi) : action.label}
                    type="button"
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Description */}
        {kpi.description && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {kpi.description}
          </p>
        )}
        {(() => {
          const tags = Array.isArray(kpi.tags)
            ? kpi.tags
            : typeof kpi.tags === 'string' && kpi.tags
              ? kpi.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
              : [];
          if (tags.length === 0) return null;
          return (
            <div className="mb-3 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-[var(--color-secondary-bg)] px-1.5 py-0.5 text-[10px] text-[var(--color-secondary-text)]"
                >
                  {tag}
                </span>
              ))}
            </div>
          );
        })()}

        {/* KPI Values */}
        <div className="space-y-3 mb-3 flex-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-secondary-text)] opacity-75">Current</span>
            <span className="text-lg font-bold text-[var(--color-secondary-text)]">
              {kpi.current_value !== undefined && kpi.current_value !== null 
                ? `${kpi.current_value}${kpi.unit ? ` ${kpi.unit}` : ''}`
                : 'N/A'}
            </span>
          </div>
          {kpi.last_updated && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
              <CalendarIcon className="w-4 h-4" />
              <span>Updated: {new Date(kpi.last_updated).toLocaleDateString()}</span>
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
};

